import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  runTransaction,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
  arrayUnion,
  arrayRemove,
  increment
} from 'firebase/firestore';
import { db, auth } from '@/lib/firebase';
import { COLLECTION_NAMES } from '@/lib/collections';
import { log } from '@/lib/logger';

const WORKFLOW_CONFIG_COLLECTION = COLLECTION_NAMES.WORKFLOW_CONFIGS;

/**
 * Description options validation constraints
 */
export const DESCRIPTION_OPTIONS_CONSTRAINTS = {
  MAX_COUNT: 10,
  MIN_LENGTH: 1,
  MAX_LENGTH: 100,
  RESERVED_WORDS: ['null', 'undefined', 'none', '(none)'],
} as const;

/**
 * Workflow configuration types
 */
export interface AllocationConfig {
  division?: string;           // Division assignment
  program: string;             // Program owner's user ID
  team_lead: string;           // Primary user lead ID
  team?: string;               // Team ID (separate from team_lead)
  support_1?: string;          // First support user ID (optional)
  support_2?: string;          // Second support user ID (optional)
  manager_override: string[];  // Array of user IDs with manager override
}

/**
 * @deprecated SpendConfigEntry is deprecated and no longer used for row population.
 * Rows are now sourced exclusively from data_submissions.spend_input.entries.
 * Kept for backward compatibility with existing Firestore documents.
 * See: teamTasksTransformUtils.ts for the single source of truth architecture.
 */
export interface SpendConfigEntry {
  channels: string;            // Channel ID from spend system
  tactics: string;             // Tactic ID from spend system
  currency: string;            // Currency code
  description?: string;        // Entry description
}

export interface SpendConfigDeal {
  deal_id: string;             // Deal identifier
  deal_name: string;           // Deal display name
  description_options?: string[];  // Array of description options (max 10)
  /**
   * @deprecated config_entries is deprecated and no longer used for row population.
   * Rows are now sourced exclusively from data_submissions.spend_input.entries.
   * Kept for backward compatibility with existing Firestore documents.
   * New rows should be added to data_submissions, not here.
   */
  config_entries?: SpendConfigEntry[];
}

export interface SpendConfig {
  deal_id?: string;            // Deal identifier (legacy)
  deal_name?: string;          // Deal display name (legacy)
  description?: string;        // Spend description (legacy)
  channels?: string[];         // Array of channel IDs from spend system (legacy)
  program?: string;            // Program owner's user ID (for spend visibility) (legacy)
  spend_config?: SpendConfigDeal[]; // New nested structure
}

interface DescriptionOptionsValidation {
  isValid: boolean;
  errors: string[];
  warnings?: string[];
}

export interface ObjectiveConfig {
  objective_type: 'performance' | 'efficiency' | 'quality' | 'growth' | 'cost-reduction';
  objective: string;           // Objective description
  target_value?: number;       // Target numeric value
  target_unit?: string;        // Unit of measurement
  project_lead?: string;       // Project lead user ID
  is_locked: boolean;          // Whether objective is locked for editing
  locked_by?: string;          // User ID who locked the objective
  locked_at?: Date;            // When objective was locked
}

export interface WorkflowConfigMetadata {
  created_at: Date;
  updated_at: Date;
  created_by: string;          // User ID who created
  createdBy: string;           // User ID who created (for security rule compatibility)
  last_modified_by: string;    // User ID who last modified
  version: number;             // For optimistic locking
  
  // Real-time collaboration
  active_editors: string[];    // Currently editing user IDs
  last_auto_save: Date;        // Last auto-save timestamp
  pending_changes: boolean;    // Has unsaved changes
}

export interface WorkflowAccessControl {
  can_edit: string[];          // User IDs with edit permissions
  can_view: string[];          // User IDs with view permissions
  program_visibility: string[]; // Program IDs with visibility
}

export interface WorkflowConfig {
  // Core workflow identity
  workflowId: string;          // BigQuery workflow ID (same as document ID)
  workflowName: string;        // Display name from BigQuery
  archived?: boolean | string | number; // Archived status from bigquery_workflows (mirrors format)
  
  // Configuration sections
  allocation_config: AllocationConfig;
  spend_config: SpendConfig;
  objective_config: ObjectiveConfig;
  
  // System data
  metadata: WorkflowConfigMetadata;
  access_control: WorkflowAccessControl;
}

export interface CreateWorkflowConfigData {
  workflowId: string;
  workflowName: string;
  allocation_config: Partial<AllocationConfig>;
  spend_config?: Partial<SpendConfig>;
  objective_config?: Partial<ObjectiveConfig>;
}

export interface UpdateWorkflowConfigData {
  workflowName?: string;
  allocation_config?: Partial<AllocationConfig>;
  spend_config?: Partial<SpendConfig>;
  objective_config?: Partial<ObjectiveConfig>;
}

/**
 * Service for managing workflow configuration data
 */
class WorkflowConfigService {
  
  /**
   * Get workflow configuration by workflow ID
   */
  async getWorkflowConfig(workflowId: string): Promise<WorkflowConfig | null> {
    try {
      const workflowRef = doc(db, WORKFLOW_CONFIG_COLLECTION, workflowId);
      const snapshot = await getDoc(workflowRef);
      
      if (!snapshot.exists()) {
        return null;
      }

      const data = snapshot.data();
      return this.convertTimestamps(data) as WorkflowConfig;
    } catch (error) {
      log.error('Error fetching workflow config:', error);
      throw new Error(`Failed to fetch workflow config for ${workflowId}`);
    }
  }

  /**
   * Create new workflow configuration
   */
  async createWorkflowConfig(
    config: CreateWorkflowConfigData,
    createdBy: string
  ): Promise<WorkflowConfig> {
    try {
      
      // Check if this is a demo user and ensure users document exists
      // Legacy user creation removed - using system_users and new auth system
      // if (createdBy.startsWith('demo-')) {
      //   await this.ensureUserExists(createdBy);
      // }
      
      // Ensure current Firebase Auth user has users document
      const currentUser = auth.currentUser;
      if (currentUser && currentUser.uid !== createdBy) {
        // Legacy user creation removed - using system_users and new auth system
        // console.log('🔧 Creating users document for current Firebase Auth user:', currentUser.uid);
        // await this.ensureUserExists(currentUser.uid);
      }
      
      const now = new Date();
      
      const defaultAllocation: AllocationConfig = {
        program: createdBy,
        team_lead: createdBy,
        manager_override: [],
        ...config.allocation_config
      };

      const defaultSpend: SpendConfig = {
        channels: [],
        program: createdBy,
        ...config.spend_config
      };

      const defaultObjective: ObjectiveConfig = {
        objective_type: 'performance',
        objective: '',
        is_locked: false,
        ...config.objective_config
      };

      const newWorkflow: WorkflowConfig = {
        workflowId: config.workflowId,
        workflowName: config.workflowName,
        allocation_config: defaultAllocation,
        spend_config: defaultSpend,
        objective_config: defaultObjective,
        metadata: {
          created_at: now,
          updated_at: now,
          created_by: createdBy,
          createdBy: createdBy, // Add this field for security rule compatibility
          last_modified_by: createdBy,
          version: 1,
          active_editors: [],
          last_auto_save: now,
          pending_changes: false
        },
        access_control: {
          can_edit: [createdBy, defaultAllocation.team_lead].filter((v, i, a) => a.indexOf(v) === i),
          can_view: [createdBy, defaultAllocation.team_lead].filter((v, i, a) => a.indexOf(v) === i),
          program_visibility: [defaultAllocation.program]
        }
      };

      const workflowRef = doc(db, WORKFLOW_CONFIG_COLLECTION, config.workflowId);
      
      await setDoc(workflowRef, this.convertToFirestore(newWorkflow));
      log.info('Workflow config document created successfully');

      return newWorkflow;
    } catch (error) {
      log.error('Error creating workflow config:', error);
      
      // Type-safe error logging
      if (error instanceof Error) {
        log.error('Error details:', {
          name: error.name,
          message: error.message,
          code: (error as any).code,
          stack: error.stack
        });
      }
      
      // Add authentication debugging
      const currentUser = auth.currentUser;
      log.error('Current Firebase Auth user:', {
        uid: currentUser?.uid,
        email: currentUser?.email,
        displayName: currentUser?.displayName,
        isAnonymous: currentUser?.isAnonymous
      });
      
      throw new Error('Failed to create workflow configuration');
    }
  }

  /**
   * Update workflow configuration
   */
  async updateWorkflowConfig(
    workflowId: string, 
    updates: UpdateWorkflowConfigData,
    modifiedBy: string,
    expectedVersion?: number
  ): Promise<void> {
    try {
      const workflowRef = doc(db, WORKFLOW_CONFIG_COLLECTION, workflowId);
      
      // Use transaction for optimistic locking if version is provided
      if (expectedVersion !== undefined) {
        await runTransaction(db, async (transaction) => {
          const doc = await transaction.get(workflowRef);
          
          if (!doc.exists()) {
            throw new Error('Workflow configuration not found');
          }

          const currentData = doc.data();
          if (currentData.metadata.version !== expectedVersion) {
            throw new Error('Workflow configuration was modified by another user');
          }

          const updateData: any = {
            ...updates,
            'metadata.updated_at': Timestamp.fromDate(new Date()),
            'metadata.last_modified_by': modifiedBy,
            'metadata.version': increment(1),
            'metadata.pending_changes': false
          };

          transaction.update(workflowRef, updateData);
        });
      } else {
        // Simple update without version check
        await updateDoc(workflowRef, {
          ...updates,
          'metadata.updated_at': Timestamp.fromDate(new Date()),
          'metadata.last_modified_by': modifiedBy,
          'metadata.version': increment(1),
          'metadata.pending_changes': false
        });
      }
    } catch (error) {
      log.error('Error updating workflow config:', error);
      throw new Error('Failed to update workflow configuration');
    }
  }

  /**
   * Update allocation configuration
   */
  async updateAllocationConfig(
    workflowId: string,
    allocation: Partial<AllocationConfig>,
    modifiedBy: string
  ): Promise<void> {
    try {
      
      const workflowRef = doc(db, WORKFLOW_CONFIG_COLLECTION, workflowId);
      
      // Build the update object with dot notation for nested fields
      const updateData: any = {
        'metadata.updated_at': Timestamp.fromDate(new Date()),
        'metadata.last_modified_by': modifiedBy,
        'metadata.version': increment(1),
        'metadata.pending_changes': false
      };

      // Add allocation config updates, converting undefined to empty string
      Object.entries(allocation).forEach(([key, value]) => {
        // Convert undefined to empty string for Firestore compatibility
        const sanitizedValue = value === undefined ? '' : value;
        updateData[`allocation_config.${key}`] = sanitizedValue;
      });

      await updateDoc(workflowRef, updateData);
    } catch (error) {
      log.error('Error updating allocation config:', error);
      throw new Error('Failed to update allocation configuration');
    }
  }

  /**
   * Update spend configuration
   */
  async updateSpendConfig(
    workflowId: string,
    spend: Partial<SpendConfig>,
    modifiedBy: string
  ): Promise<void> {
    try {
      
      const workflowRef = doc(db, WORKFLOW_CONFIG_COLLECTION, workflowId);
      
      // Build the update object with dot notation for nested fields
      const updateData: any = {
        'metadata.updated_at': Timestamp.fromDate(new Date()),
        'metadata.last_modified_by': modifiedBy,
        'metadata.version': increment(1),
        'metadata.pending_changes': false
      };

      // Add spend config updates, converting undefined to empty string
      Object.entries(spend).forEach(([key, value]) => {
        // Convert undefined to empty string for Firestore compatibility
        const sanitizedValue = value === undefined ? '' : value;
        updateData[`spend_config.${key}`] = sanitizedValue;
      });

      await updateDoc(workflowRef, updateData);
    } catch (error) {
      log.error('Error updating spend config:', error);
      throw new Error('Failed to update spend configuration');
    }
  }

  /**
   * Update objective configuration
   */
  async updateObjectiveConfig(
    workflowId: string,
    objective: Partial<ObjectiveConfig>,
    modifiedBy: string
  ): Promise<void> {
    try {
      
      const workflowRef = doc(db, WORKFLOW_CONFIG_COLLECTION, workflowId);
      
      // Build the update object with dot notation for nested fields
      const updateData: any = {
        'metadata.updated_at': Timestamp.fromDate(new Date()),
        'metadata.last_modified_by': modifiedBy,
        'metadata.version': increment(1),
        'metadata.pending_changes': false
      };

      // Add objective config updates, converting undefined to appropriate values
      Object.entries(objective).forEach(([key, value]) => {
        if (key === 'locked_at' && value instanceof Date) {
          updateData[`objective_config.${key}`] = Timestamp.fromDate(value);
        } else {
          // Convert undefined to empty string for Firestore compatibility
          const sanitizedValue = value === undefined ? '' : value;
          updateData[`objective_config.${key}`] = sanitizedValue;
        }
      });

      await updateDoc(workflowRef, updateData);
    } catch (error) {
      log.error('Error updating objective config:', error);
      throw new Error('Failed to update objective configuration');
    }
  }

  /**
   * Delete workflow configuration
   */
  async deleteWorkflowConfig(workflowId: string): Promise<void> {
    try {
      const workflowRef = doc(db, WORKFLOW_CONFIG_COLLECTION, workflowId);
      await deleteDoc(workflowRef);
    } catch (error) {
      log.error('Error deleting workflow config:', error);
      throw new Error('Failed to delete workflow configuration');
    }
  }

  /**
   * Get workflows by program owner ID
   */
  async getWorkflowsByProgram(programOwnerId: string): Promise<WorkflowConfig[]> {
    try {
      const workflowsRef = collection(db, WORKFLOW_CONFIG_COLLECTION);
      const q = query(
        workflowsRef,
        where('allocation_config.program', '==', programOwnerId),
        orderBy('workflowName', 'asc')
      );
      const snapshot = await getDocs(q);
      
      const configs = snapshot.docs.map(doc => this.convertTimestamps(doc.data())) as WorkflowConfig[];
      
      // Filter out archived workflows (archived === 1, "1", or true)
      const activeConfigs = configs.filter(config => {
        const archived = config.archived;
        return !(archived === 1 || archived === "1" || archived === true);
      });
      
      return activeConfigs;
    } catch (error) {
      log.error('Error fetching workflows by program:', error);
      throw new Error('Failed to fetch workflows for program');
    }
  }

  /**
   * Get all workflow configurations (admin function)
   */
  async getAllWorkflowConfigs(): Promise<WorkflowConfig[]> {
    try {
      const workflowsRef = collection(db, WORKFLOW_CONFIG_COLLECTION);
      
      // Try without orderBy first to see if that's causing issues
      let snapshot;
      try {
        snapshot = await getDocs(workflowsRef);
      } catch (simpleError) {
        throw simpleError;
      }
      
      // If simple query works, try with orderBy
      if (snapshot.docs.length > 0) {
        try {
          const q = query(workflowsRef, orderBy('workflowName', 'asc'));
          snapshot = await getDocs(q);
        } catch (orderError) {
          // Keep the simple snapshot
        }
      }
      
      const configs = snapshot.docs.map(doc => {
        const data = doc.data();
        return this.convertTimestamps(data);
      }) as WorkflowConfig[];
      
      // Filter out archived workflows (archived === 1, "1", or true)
      const activeConfigs = configs.filter(config => {
        const archived = config.archived;
        return !(archived === 1 || archived === "1" || archived === true);
      });
      
      return activeConfigs;
    } catch (error) {
      log.error('Error fetching all workflow configs:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      log.error('Error details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: errorMessage,
        code: (error as any)?.code || 'Unknown',
        stack: error instanceof Error ? error.stack : 'No stack trace'
      });
      throw new Error(`Failed to fetch workflow configurations: ${errorMessage}`);
    }
  }

  /**
   * Get workflows where user has access
   */
  async getWorkflowsForUser(userId: string): Promise<WorkflowConfig[]> {
    try {
      const workflowsRef = collection(db, WORKFLOW_CONFIG_COLLECTION);
      const q = query(
        workflowsRef, 
        where('access_control.can_view', 'array-contains', userId),
        orderBy('workflowName', 'asc')
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => this.convertTimestamps(doc.data())) as WorkflowConfig[];
    } catch (error) {
      log.error('Error fetching workflows for user:', error);
      throw new Error('Failed to fetch user workflows');
    }
  }

  /**
   * Subscribe to workflow configuration changes
   */
  subscribeToWorkflowConfig(
    workflowId: string, 
    callback: (config: WorkflowConfig | null) => void
  ): () => void {
    const workflowRef = doc(db, WORKFLOW_CONFIG_COLLECTION, workflowId);
    
    return onSnapshot(workflowRef, (snapshot) => {
      if (!snapshot.exists()) {
        callback(null);
        return;
      }

      const data = snapshot.data();
      const config = this.convertTimestamps(data) as WorkflowConfig;
      callback(config);
    }, (error) => {
      log.error('Error in workflow config subscription:', error);
      callback(null);
    });
  }

  /**
   * Subscribe to workflows by program
   */
  subscribeToWorkflowsByProgram(
    programOwnerId: string,
    callback: (configs: WorkflowConfig[]) => void
  ): () => void {
    const workflowsRef = collection(db, WORKFLOW_CONFIG_COLLECTION);
    const q = query(
      workflowsRef, 
      where('allocation_config.program', '==', programOwnerId),
      orderBy('workflowName', 'asc')
    );
    
    return onSnapshot(q, (snapshot) => {
      const configs = snapshot.docs.map(doc => 
        this.convertTimestamps(doc.data())
      ) as WorkflowConfig[];
      
      callback(configs);
    }, (error) => {
      log.error('Error in program workflows subscription:', error);
      callback([]);
    });
  }

  /**
   * Subscribe to workflows for user
   */
  subscribeToWorkflowsForUser(
    userId: string,
    callback: (configs: WorkflowConfig[]) => void
  ): () => void {
    const workflowsRef = collection(db, WORKFLOW_CONFIG_COLLECTION);
    const q = query(
      workflowsRef, 
      where('access_control.can_view', 'array-contains', userId),
      orderBy('workflowName', 'asc')
    );
    
    return onSnapshot(q, (snapshot) => {
      const configs = snapshot.docs.map(doc => 
        this.convertTimestamps(doc.data())
      ) as WorkflowConfig[];
      
      callback(configs);
    }, (error) => {
      log.error('Error in user workflows subscription:', error);
      callback([]);
    });
  }

  /**
   * Subscribe to all workflow configurations (admin function)
   */
  subscribeToAllWorkflowConfigs(
    callback: (configs: WorkflowConfig[]) => void
  ): () => void {
    const workflowsRef = collection(db, WORKFLOW_CONFIG_COLLECTION);
    
    return onSnapshot(workflowsRef, (snapshot) => {
      
      const configs = snapshot.docs.map(doc => {
        const data = doc.data();
        return this.convertTimestamps(data);
      }) as WorkflowConfig[];
      
      // Sort in memory instead of using Firestore orderBy
      configs.sort((a, b) => (a.workflowName || '').localeCompare(b.workflowName || ''));
      callback(configs);
    }, (error) => {
      log.error('Error in all workflows subscription:', error);
      callback([]);
    });
  }

  /**
   * Track active editor for real-time collaboration
   */
  async trackActiveEditor(workflowId: string, userId: string): Promise<void> {
    try {
      const workflowRef = doc(db, WORKFLOW_CONFIG_COLLECTION, workflowId);
      await updateDoc(workflowRef, {
        'metadata.active_editors': arrayUnion(userId),
        'metadata.last_auto_save': Timestamp.fromDate(new Date())
      });
    } catch (error) {
      log.error('Error tracking active editor:', error);
      throw new Error('Failed to track active editor');
    }
  }

  /**
   * Remove active editor
   */
  async removeActiveEditor(workflowId: string, userId: string): Promise<void> {
    try {
      const workflowRef = doc(db, WORKFLOW_CONFIG_COLLECTION, workflowId);
      await updateDoc(workflowRef, {
        'metadata.active_editors': arrayRemove(userId)
      });
    } catch (error) {
      log.error('Error removing active editor:', error);
      throw new Error('Failed to remove active editor');
    }
  }

  /**
   * Mark workflow as having pending changes
   */
  async markPendingChanges(workflowId: string, hasPendingChanges: boolean): Promise<void> {
    try {
      const workflowRef = doc(db, WORKFLOW_CONFIG_COLLECTION, workflowId);
      await updateDoc(workflowRef, {
        'metadata.pending_changes': hasPendingChanges,
        'metadata.last_auto_save': Timestamp.fromDate(new Date())
      });
    } catch (error) {
      log.error('Error marking pending changes:', error);
      throw new Error('Failed to mark pending changes');
    }
  }

  /**
   * Lock/unlock objective for editing
   */
  async lockObjective(
    workflowId: string, 
    isLocked: boolean, 
    lockedBy?: string
  ): Promise<void> {
    try {
      const workflowRef = doc(db, WORKFLOW_CONFIG_COLLECTION, workflowId);
      const updateData: any = {
        'objective_config.is_locked': isLocked,
        'metadata.updated_at': Timestamp.fromDate(new Date()),
        'metadata.version': increment(1)
      };

      if (isLocked && lockedBy) {
        updateData['objective_config.locked_by'] = lockedBy;
        updateData['objective_config.locked_at'] = Timestamp.fromDate(new Date());
      } else {
        updateData['objective_config.locked_by'] = null;
        updateData['objective_config.locked_at'] = null;
      }

      await updateDoc(workflowRef, updateData);
    } catch (error) {
      log.error('Error locking/unlocking objective:', error);
      throw new Error('Failed to lock/unlock objective');
    }
  }

  /**
   * Batch create multiple workflow configurations
   */
  async batchCreateWorkflowConfigs(
    workflows: CreateWorkflowConfigData[],
    createdBy: string
  ): Promise<void> {
    try {
      const batch = writeBatch(db);
      const now = new Date();

      for (const config of workflows) {
        const defaultAllocation: AllocationConfig = {
          program: createdBy,
          team_lead: createdBy,
          manager_override: [],
          ...config.allocation_config
        };

        const defaultSpend: SpendConfig = {
          channels: [],
          program: createdBy,
          ...config.spend_config
        };

        const defaultObjective: ObjectiveConfig = {
          objective_type: 'performance',
          objective: '',
          is_locked: false,
          ...config.objective_config
        };

        const workflowData: WorkflowConfig = {
          workflowId: config.workflowId,
          workflowName: config.workflowName,
          allocation_config: defaultAllocation,
          spend_config: defaultSpend,
          objective_config: defaultObjective,
          metadata: {
            created_at: now,
            updated_at: now,
            created_by: createdBy,
            createdBy: createdBy, // Add this field for security rule compatibility
            last_modified_by: createdBy,
            version: 1,
            active_editors: [],
            last_auto_save: now,
            pending_changes: false
          },
          access_control: {
            can_edit: [createdBy, defaultAllocation.team_lead].filter((v, i, a) => a.indexOf(v) === i),
            can_view: [createdBy, defaultAllocation.team_lead].filter((v, i, a) => a.indexOf(v) === i),
            program_visibility: [defaultAllocation.program]
          }
        };

        const workflowRef = doc(db, WORKFLOW_CONFIG_COLLECTION, config.workflowId);
        batch.set(workflowRef, this.convertToFirestore(workflowData));
      }

      await batch.commit();
    } catch (error) {
      log.error('Error batch creating workflow configs:', error);
      throw new Error('Failed to batch create workflow configurations');
    }
  }

  /**
   * Check if user can edit workflow
   */
  async canEditWorkflow(workflowId: string, userId: string): Promise<boolean> {
    try {
      const config = await this.getWorkflowConfig(workflowId);
      if (!config) return false;

      return config.access_control.can_edit.includes(userId);
    } catch (error) {
      log.error('Error checking edit permissions:', error);
      return false;
    }
  }

  /**
   * Check if user can view workflow
   */
  async canViewWorkflow(workflowId: string, userId: string): Promise<boolean> {
    try {
      const config = await this.getWorkflowConfig(workflowId);
      if (!config) return false;

      return config.access_control.can_view.includes(userId);
    } catch (error) {
      log.error('Error checking view permissions:', error);
      return false;
    }
  }

  /**
   * Update description options for a workflow's deal
   */
  async updateDescriptionOptions(
    workflowId: string,
    dealIndex: number,
    options: string[],
    modifiedBy: string
  ): Promise<void> {
    try {
      // Validate options first
      const validation = this.validateDescriptionOptions(options);
      if (!validation.isValid) {
        throw new Error(`Invalid description options: ${validation.errors.join(', ')}`);
      }

      const workflowRef = doc(db, WORKFLOW_CONFIG_COLLECTION, workflowId);
      
      // Read the current document to preserve existing data
      // Note: config_entries is deprecated but preserved for backward compatibility with existing documents
      const docSnap = await getDoc(workflowRef);
      if (!docSnap.exists()) {
        throw new Error(`Workflow config ${workflowId} not found`);
      }

      const currentData = docSnap.data();
      let spendConfigArray = currentData.spend_config;

      // Handle nested structure (spend_config.spend_config) for backward compatibility
      const isNestedStructure = !Array.isArray(spendConfigArray) && spendConfigArray?.spend_config;
      if (isNestedStructure) {
        const innerValue = spendConfigArray.spend_config;
        
        // Check if it's an array or object-with-numeric-keys (Firestore sometimes converts arrays)
        if (Array.isArray(innerValue)) {
          spendConfigArray = innerValue;
        } else if (innerValue && typeof innerValue === 'object') {
          // Check if it's an object with numeric keys (converted array)
          const keys = Object.keys(innerValue);
          const hasNumericKeys = keys.length > 0 && keys.every(k => !isNaN(Number(k)));
          
          if (hasNumericKeys) {
            // Convert object with numeric keys back to array
            spendConfigArray = keys
              .sort((a, b) => Number(a) - Number(b))
              .map(k => innerValue[k]);
          } else {
            spendConfigArray = innerValue;
          }
        } else {
          spendConfigArray = innerValue;
        }
      }

      if (!Array.isArray(spendConfigArray) || spendConfigArray.length <= dealIndex) {
        throw new Error(`Invalid spend_config structure or dealIndex ${dealIndex} out of bounds`);
      }

      // Merge description_options into the existing deal object, preserving all other fields
      // Note: config_entries is deprecated but preserved here for backward compatibility
      const updatedDeal = {
        ...spendConfigArray[dealIndex],
        description_options: options
      };

      // Create updated array
      const updatedSpendConfigArray = [...spendConfigArray];
      updatedSpendConfigArray[dealIndex] = updatedDeal;

      // Build the update object
      const updateData: any = {
        'metadata.updated_at': Timestamp.fromDate(new Date()),
        'metadata.last_modified_by': modifiedBy,
        'metadata.version': increment(1),
        'metadata.pending_changes': false
      };

      // Set the correct path based on data structure
      if (isNestedStructure) {
        updateData['spend_config.spend_config'] = updatedSpendConfigArray;
      } else {
        updateData['spend_config'] = updatedSpendConfigArray;
      }

      await updateDoc(workflowRef, updateData);
    } catch (error) {
      log.error('Error updating description options:', error);
      throw new Error('Failed to update description options');
    }
  }

  /**
   * Get description options for a workflow
   * Handles both array and nested spend_config structures for backward compatibility
   */
  async getDescriptionOptions(workflowId: string): Promise<string[]> {
    try {
      const config = await this.getWorkflowConfig(workflowId);
      if (!config) {
        return [];
      }

      // Check nested structure first (new format)
      if (config.spend_config.spend_config && Array.isArray(config.spend_config.spend_config)) {
        const deals = config.spend_config.spend_config as SpendConfigDeal[];
        if (deals.length > 0 && deals[0].description_options) {
          return deals[0].description_options;
        }
      }

      // Return empty array if not found
      return [];
    } catch (error) {
      log.error('Error getting description options:', error);
      return [];
    }
  }

  /**
   * Validate description options array
   */
  validateDescriptionOptions(options: string[]): DescriptionOptionsValidation {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check max count
    if (options.length > DESCRIPTION_OPTIONS_CONSTRAINTS.MAX_COUNT) {
      errors.push(`Maximum ${DESCRIPTION_OPTIONS_CONSTRAINTS.MAX_COUNT} options allowed`);
    }

    // Check each option
    const seenOptions = new Set<string>();
    options.forEach((option, index) => {
      const trimmed = option.trim();

      // Empty check
      if (trimmed.length === 0) {
        errors.push(`Option at index ${index} is empty`);
        return;
      }

      // Length check
      if (trimmed.length < DESCRIPTION_OPTIONS_CONSTRAINTS.MIN_LENGTH) {
        errors.push(`Option "${trimmed}" is too short (min ${DESCRIPTION_OPTIONS_CONSTRAINTS.MIN_LENGTH} character)`);
      }

      if (trimmed.length > DESCRIPTION_OPTIONS_CONSTRAINTS.MAX_LENGTH) {
        errors.push(`Option "${trimmed}" is too long (max ${DESCRIPTION_OPTIONS_CONSTRAINTS.MAX_LENGTH} characters)`);
      }

      // Duplicate check (case-insensitive)
      const lowerOption = trimmed.toLowerCase();
      if (seenOptions.has(lowerOption)) {
        errors.push(`Duplicate option: "${trimmed}"`);
      }
      seenOptions.add(lowerOption);

      // Reserved words warning
      const isReserved = DESCRIPTION_OPTIONS_CONSTRAINTS.RESERVED_WORDS.some(
        word => lowerOption === word.toLowerCase()
      );
      if (isReserved) {
        warnings.push(`"${trimmed}" is a reserved word - use with caution`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
      warnings: warnings.length > 0 ? warnings : undefined
    };
  }

  /**
   * Convert Firestore timestamps to Date objects
   */
  private convertTimestamps(data: any): any {
    const converted = { ...data };
    
    if (converted.metadata) {
      if (converted.metadata.created_at?.toDate) {
        converted.metadata.created_at = converted.metadata.created_at.toDate();
      }
      if (converted.metadata.updated_at?.toDate) {
        converted.metadata.updated_at = converted.metadata.updated_at.toDate();
      }
      if (converted.metadata.last_auto_save?.toDate) {
        converted.metadata.last_auto_save = converted.metadata.last_auto_save.toDate();
      }
    }

    if (converted.objective_config?.locked_at?.toDate) {
      converted.objective_config.locked_at = converted.objective_config.locked_at.toDate();
    }

    return converted;
  }

  /**
   * Convert Date objects to Firestore timestamps
   */
  private convertToFirestore(data: WorkflowConfig): any {
    const converted: any = { ...data };
    
    if (converted.metadata) {
      // Preserve all metadata fields, only convert Date objects to Timestamps
      converted.metadata = {
        ...converted.metadata, // Keep all existing fields including createdBy
        created_at: Timestamp.fromDate(converted.metadata.created_at),
        updated_at: Timestamp.fromDate(converted.metadata.updated_at),
        last_auto_save: Timestamp.fromDate(converted.metadata.last_auto_save)
      };
    }

    if (converted.objective_config.locked_at) {
      converted.objective_config = {
        ...converted.objective_config,
        locked_at: Timestamp.fromDate(converted.objective_config.locked_at)
      };
    }

    return converted;
  }
}

// Create and export service instance
export const workflowConfigService = new WorkflowConfigService();